// =============================================================================
// Business config — single source of truth for the wireframe template.
// CLIENT: Drain Sights (Baltimore, MD)
//
// DRAIN & SEWER SPECIALISTS — not full-service plumbers. Service-area business
// run by two crews:
//   • North hub  — Baltimore (311 6th Ave, Baltimore, MD 21225)  [mailing addr]
//   • South hub  — Temple Hills, MD (Carlton Ave, 20748)
// Hours model: 24/7 EMERGENCY SERVICE — open 24 hours, 7 days a week.
//   - phone: (240) 332-5647 · email: gotdrain@gmail.com
//   - SERVICE_AREAS = 6 biggest cities near each hub (12 total)
//   - Reviews now come from the embedded GoHighLevel Google reviews widget;
//     TESTIMONIALS / GOOGLE_REVIEWS below are legacy demo data kept only so any
//     remaining imports don't break — they are no longer rendered on the site.
// =============================================================================

export const BUSINESS = {
  name: "Drain Sights",
  shortName: "Drain Sights",
  tagline: "We Sight in on Your Problems",
  phone: "(240) 332-5647",
  phoneHref: "tel:+12403325647",
  email: "gotdrain@gmail.com",
  emailHref: "mailto:gotdrain@gmail.com",
  // Physical mailing address — shown ONLY on the Contact page (service-area
  // business: we don't advertise a storefront address site-wide).
  address: "311 6th Ave, Baltimore, MD 21225",
  streetAddress: "311 6th Ave",
  city: "Baltimore",
  state: "MD",
  zip: "21225",
  county: "Baltimore County",
  geo: { lat: 39.2285157, lng: -76.6189962 },
  hours: {
    display: "24/7 Emergency Service",
    detail: "Open 24 Hours · 7 Days a Week",
    schema: "Mo-Su 00:00-23:59",
    weekdays: "24/7 Emergency Service",
    saturday: "Open 24 hours",
    sunday: "Open 24 hours",
    note: "Available 24/7 for drain & sewer emergencies",
  },
  social: {
    instagram: "https://www.facebook.com/p/Drain-Sights-LLC-61579186844904/",
    facebook: "https://www.facebook.com/p/Drain-Sights-LLC-61579186844904/",
  },
  license: "Licensed & Insured · 30+ years experience · Drain & sewer specialists",
};

// ---------------------------------------------------------------------------
// SEO / canonical configuration
// ---------------------------------------------------------------------------
// Canonical host — NON-www is the single source of truth. Every canonical,
// OG url, sitemap entry, and JSON-LD url must use this exact host. Never mix.
export const SITE_URL = "https://getdrainsights.com";

// Absolute, branded image used for social sharing (Open Graph / Twitter).
export const OG_IMAGE = `${SITE_URL}/drain-sights-truck.png`;

// Distinct real profiles for schema `sameAs`.
export const SOCIAL_PROFILES = [
  "https://www.facebook.com/p/Drain-Sights-LLC-61579186844904/",
  "https://www.google.com/maps/place/Drain+Sights/data=!4m2!3m1!1s0x0:0x4937d286f6772ecd",
];

// Real Google rating. `count` stays null until confirmed with the client so we
// never publish a fabricated review count in structured data.
// Google rating is now sourced dynamically from the GoHighLevel review widget
// (synced to the Google Business Profile), so we intentionally do NOT hardcode
// an AggregateRating in our own JSON-LD — that avoids stale/conflicting numbers.
// Kept here only for optional on-site display; leave count null to avoid drift.
export const GOOGLE_RATING = { value: "4.9", count: null };

// Founding date (business established) — ISO 8601 for schema `foundingDate`.
export const FOUNDING_DATE = "2010-01-21";

// SERVICE AREAS — two hubs, 6 biggest communities within ~10 miles of each.
// North (Baltimore 21225) first, then South (Temple Hills 20748).
export const SERVICE_AREAS = [
  // --- North hub: Baltimore / 311 6th Ave ---
  { slug: "baltimore-md", city: "Baltimore", state: "MD", primary: true, blurb: "Our north-side home base — serving Baltimore City and the surrounding neighborhoods." },
  { slug: "dundalk-md", city: "Dundalk", state: "MD", blurb: "Drain cleaning, camera inspection, and sewer work across Dundalk." },
  { slug: "glen-burnie-md", city: "Glen Burnie", state: "MD", blurb: "Trusted drain and sewer service for Glen Burnie homes." },
  { slug: "catonsville-md", city: "Catonsville", state: "MD", blurb: "Camera inspections, jetting, and line repair throughout Catonsville." },
  { slug: "pasadena-md", city: "Pasadena", state: "MD", blurb: "Sewer and drain specialists serving Pasadena." },
  { slug: "arbutus-md", city: "Arbutus", state: "MD", blurb: "Fast drain and sewer help for Arbutus." },
  // --- South hub: Temple Hills / Carlton Ave ---
  { slug: "washington-dc", city: "Washington", state: "DC", blurb: "Drain cleaning, jetting, and sewer service across Washington, DC." },
  { slug: "clinton-md", city: "Clinton", state: "MD", blurb: "Trusted drain and sewer specialists in Clinton." },
  { slug: "suitland-md", city: "Suitland", state: "MD", blurb: "Camera inspections and jetting throughout Suitland." },
  { slug: "fort-washington-md", city: "Fort Washington", state: "MD", blurb: "Sewer and drain service for Fort Washington homes." },
  { slug: "camp-springs-md", city: "Camp Springs", state: "MD", blurb: "Drain and sewer help across Camp Springs." },
  { slug: "oxon-hill-md", city: "Oxon Hill", state: "MD", blurb: "Fast, clean drain and sewer service in Oxon Hill." },
];

// SERVICES — drain & sewer ONLY. Listed in order of importance (drain/sewer
// first). The first item is the featured service.
export const SERVICES = [
  { slug: "drain-cleaning", name: "Drain Cleaning", category: "Drains & Sewers",
    short: "Slow or clogged drains cleared fast — kitchen, bath, and main lines.",
    icon: "Waves", featured: true },
  { slug: "sewer-camera-inspection", name: "Sewer Camera Inspection & Line Locating", category: "Drains & Sewers",
    short: "We see inside your pipes and pinpoint the line — no guesswork digging.", icon: "Camera" },
  { slug: "hydro-jetting", name: "Hydro-Jetting", category: "Drains & Sewers",
    short: "High-pressure jetting that scours grease, scale, and roots from your lines.", icon: "Droplets" },
  { slug: "pipe-descaling", name: "Pipe Descaling", category: "Drains & Sewers",
    short: "Mechanical descaling that restores old cast-iron pipes back to full flow.", icon: "Pipette" },
  { slug: "sewer-waterline-replacement", name: "Sewer & Waterline Replacements", category: "Drains & Sewers",
    short: "Failing sewer and water lines replaced and restored — done right.", icon: "Wrench" },
  { slug: "sump-pump-replacement", name: "Sump Pump Replacements", category: "Water & Basement",
    short: "Worn or failed sump pumps swapped out to keep your basement dry.", icon: "Droplet" },
  { slug: "basement-water-pumping", name: "Basement Water Pumping", category: "Water & Basement",
    short: "Standing water pumped out fast after a backup or heavy rain.", icon: "ArrowUpFromLine" },
  { slug: "basement-waterproofing", name: "Basement Waterproofing", category: "Water & Basement",
    short: "Stop water at the source and keep your basement dry for good.", icon: "Umbrella" },
  { slug: "underground-gutters", name: "Underground Gutters Cleaning & Replacement", category: "Water & Basement",
    short: "Clogged or broken downspout drains cleared, repaired, or replaced.", icon: "Pipette" },
  { slug: "mini-excavator-service", name: "Mini Bobcat & Mini Excavator Service", category: "Excavation & Hauling",
    short: "Compact excavation for sewer lines, drainage, and tight-access digs.", icon: "Shovel" },
  { slug: "junk-trash-removal", name: "Junk & Trash Removal", category: "Excavation & Hauling",
    short: "Haul-away for debris, old fixtures, and basement cleanout junk.", icon: "Trash2" },
];

export const SERVICE_CATEGORIES = [
  "Drains & Sewers",
  "Water & Basement",
  "Excavation & Hauling",
];

// Public Google Business Profile reviews link (used by the GHL reviews widget area / CTAs).
export const GBP_REVIEWS_URL = "https://www.google.com/search?sca_esv=347e5054d04bd701&q=Drain+Sights+Reviews";

export const HERO_TRUST_BADGES = [
  { title: "30+ Years Experience", desc: "Three decades of drain, sewer & HVAC expertise across Maryland." },
  { title: "Upfront Pricing", desc: "Clear, written estimates before any work begins." },
  { title: "Licensed & Insured", desc: "A local contractor you can actually talk to." },
];

// Common homeowner issues — drain & sewer focused, no emergency / 24/7 framing.
export const PROBLEMS = [
  { title: "Clogged Drain", desc: "Slow sink, tub, or shower? We clear it for good." },
  { title: "Recurring Backups", desc: "Same drain backing up again? There's a deeper cause." },
  { title: "Sewer Line Backup", desc: "Gurgles and odors often mean a main-line issue." },
  { title: "Tree Root Intrusion", desc: "Roots in the line? Jetting and cameras find them." },
  { title: "Wet Basement", desc: "Standing water or seepage? We pump and waterproof it." },
  { title: "Failed Sump Pump", desc: "Pump not keeping up? We replace it before the next storm." },
];

export const WHY_CHOOSE = [
  { title: "Two Local Crews", desc: "Based in the Baltimore and Temple Hills metros, so help is never far away." },
  { title: "Clear Communication", desc: "We explain the problem in plain English and walk you through every option." },
  { title: "Upfront Estimates", desc: "You see the price before we start. No surprise charges, ever." },
  { title: "Clean Workmanship", desc: "Shoe covers, drop cloths, and a clean job site when we leave." },
  { title: "Licensed & Insured", desc: "Local drain & sewer contractor with full insurance." },
  { title: "Drain & Sewer Specialists", desc: "Camera inspection, hydro-jetting, descaling, and line replacement — our only work." },
];

export const PROCESS = [
  { step: "1", title: "Call or Request a Quote", desc: "Tell us what's going on — we answer 24/7, day or night.", icon: "PhoneCall" },
  { step: "2", title: "We Inspect the Line", desc: "A camera finds the real cause before anyone starts digging.", icon: "Search" },
  { step: "3", title: "You Get a Clear Estimate", desc: "Honest, upfront pricing before any work begins.", icon: "FileCheck2" },
  { step: "4", title: "We Complete the Work", desc: "Clean, careful, and done right the first time.", icon: "Wrench" },
];

// --- Legacy demo review data (no longer rendered — reviews come from the GHL
//     widget). Kept so leftover imports compile. ---
export const TESTIMONIALS = [
  { name: "Sarah M.", city: "Baltimore, MD", rating: 5, service: "Drain Cleaning", date: "2 weeks ago",
    quote: "Cleared a kitchen drain that kept backing up. Quick, clean, and they showed me the camera footage." },
  { name: "Derrick R.", city: "Clinton, MD", rating: 5, service: "Sewer Camera Inspection", date: "1 month ago",
    quote: "Ran a camera down the main line and found roots two other companies missed. Honest and on time." },
  { name: "Priya S.", city: "Glen Burnie, MD", rating: 5, service: "Hydro-Jetting", date: "3 weeks ago",
    quote: "Jetted out years of grease buildup. The line drains like new now." },
];

export const GOOGLE_REVIEWS = [
  { name: "Sarah M.", initials: "SM", color: "#54616E", rating: 5, date: "2 weeks ago",
    quote: "Cleared a clogged kitchen drain quickly and cleanly. No surprise charges." },
  { name: "Derrick R.", initials: "DR", color: "#1A1D21", rating: 5, date: "1 month ago",
    quote: "Camera inspection found roots two other companies missed. Honest, on time, fair price." },
  { name: "Priya S.", initials: "PS", color: "#E31D2B", rating: 5, date: "3 weeks ago",
    quote: "Hydro-jetting brought our slow line back to life." },
];

// Portfolio — professional drain & sewer stock imagery that matches each service.
export const PORTFOLIO = [
  { title: "Main Line Drain Cleaning", category: "Drain Cleaning", city: "Baltimore, MD",
    image: "https://images.pexels.com/photos/32588548/pexels-photo-32588548.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { title: "Sewer Camera Inspection", category: "Camera Inspection", city: "Glen Burnie, MD",
    image: "https://images.pexels.com/photos/17375718/pexels-photo-17375718.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { title: "Hydro-Jetting a Grease Line", category: "Hydro-Jetting", city: "Dundalk, MD",
    image: "https://images.pexels.com/photos/36842620/pexels-photo-36842620.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { title: "Cracked Sewer Line Replacement", category: "Sewer Replacement", city: "Clinton, MD",
    image: "https://images.pexels.com/photos/37627673/pexels-photo-37627673.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { title: "Sump Pump Replacement", category: "Water & Basement", city: "Fort Washington, MD",
    image: "https://images.pexels.com/photos/29226620/pexels-photo-29226620.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { title: "Sewer Line Excavation", category: "Excavation", city: "Suitland, MD",
    image: "https://images.pexels.com/photos/31314903/pexels-photo-31314903.jpeg?auto=compress&cs=tinysrgb&w=1200" },
];

// =============================================================================
// FAQ BANK — single source of truth for every FAQ section on the site.
// Each FAQ has a stable `id` so it can be reused across the /faq page, the home
// "quick answers" block, and the per-service FAQ sections (see getServiceFaqs).
// Positioning note: Drain Sights is a LICENSED CONTRACTOR (drain/sewer clearing,
// waterproofing, excavation) — NOT a full-service plumbing company. Keep that
// wording consistent everywhere.
// =============================================================================
export const FAQ_CATEGORIES = [
  {
    name: "General",
    faqs: [
      { id: "gen-services", q: "What services do you offer?", a: "We specialize in drain cleaning, sewer camera inspection, and hydro-jetting, plus basement waterproofing and mini excavator work such as sewer line dig-outs and drainage trenching." },
      { id: "gen-areas", q: "What areas do you serve?", a: "We serve Baltimore, Dundalk, Glen Burnie, Catonsville, Pasadena, Arbutus, Washington D.C. and the surrounding metro counties. Call us at (240) 332-5647 or visit our ", link: { to: "/service-areas", label: "Service Areas page" }, aEnd: " if you're not sure whether you're in our area." },
      { id: "gen-licensed-insured", q: "Are you licensed and insured?", a: "Yes. We're a licensed contractor and fully insured for the work we perform." },
      { id: "gen-licensed-plumber", q: "Are you a licensed plumber?", a: "No — we're a licensed contractor that specializes in drain cleaning, waterproofing, and excavation, not a full service plumbing company. We specialize in mechanical drain clearing (snaking, jetting, and camera inspection)." },
      { id: "gen-contractor-vs-plumber", q: "What's the difference between a drain cleaning contractor and a plumber?", a: "A drain contractor clears and inspects existing drain and sewer lines — removing clogs, roots, and buildup, and diagnosing problems with a camera. A licensed plumber installs and alters the plumbing system itself (supply lines, gas lines, fixtures, repiping). We handle the drain and sewer side; anything that requires altering the plumbing system, we refer out." },
      { id: "gen-refer-plumber", q: "What kind of work would you refer to a licensed plumber?", a: "Things like repiping, installing or moving fixtures, water heater work, water-supply lines, and gas lines. If your issue turns out to need that, we'll let you know rather than work outside our lane." },
      { id: "gen-how-long", q: "How long have you been in business?", a: "We've been serving the Baltimore and Washington D.C. metro area since 2010." },
      { id: "gen-free-estimates", q: "Do you offer free estimates?", a: "Yes — we provide free estimates for most jobs. Diagnostic camera inspections among other inspections may carry a fee, which is credited toward the repair." },
      { id: "gen-schedule", q: "How do I schedule service?", a: "Call or text us at (240) 332-5647, or request an appointment through our website." },
      { id: "gen-emergencies", q: "Do you handle emergencies or after-hours calls?", a: "Yes — we offer 24/7 emergency and after-hours service for backups and active leaks. Emergency rates may apply." },
    ],
  },
  {
    name: "Drain Cleaning",
    faqs: [
      { id: "drain-what-is", q: "What is drain cleaning (snaking)?", a: "It's the mechanical clearing of a clogged or slow drain using a cable auger (\"snake\") to break up or pull out the blockage and restore flow." },
      { id: "drain-causes", q: "What causes a clogged drain?", a: "Common causes are grease, hair, soap scum, food waste, \"flushable\" wipes, foreign objects, tree roots, and long-term buildup inside the pipe walls." },
      { id: "drain-small-vs-sewer", q: "How do I know if it's a small clog or a bigger sewer problem?", a: "If one fixture is slow, it's usually a local clog. If multiple fixtures back up at once, or you hear gurgling and see water at the lowest drain, that often points to a main sewer line issue — a camera inspection tells us for sure." },
      { id: "drain-kitchen", q: "Can you clear kitchen sink clogs?", a: "Yes — kitchen lines clog often from grease and food buildup, and we clear them regularly." },
      { id: "drain-bath", q: "Can you clear bathroom, shower, and tub drains?", a: "Yes. These are typically hair and soap buildup, which we clear by snaking the line." },
      { id: "drain-main", q: "Can you clear a main sewer line clog?", a: "Yes — main line backups are one of our core jobs. We can also camera the line afterward to confirm the cause." },
      { id: "drain-howlong", q: "How long does a typical drain cleaning take?", a: "Most straightforward clogs take under an hour. Main line and heavily built-up lines can take longer." },
      { id: "drain-chemical", q: "Do store-bought chemical drain cleaners work? Should I use them?", a: "We don't recommend them. They often don't fully clear the clog, can damage older pipes, and create a hazard for whoever services the line afterward. Mechanical clearing is safer and more reliable." },
      { id: "drain-keeps-clogging", q: "Why does my drain keep clogging?", a: "Repeat clogs usually mean there's buildup, a partial root intrusion, a sag or \"belly\" in the line, or a pipe defect. A camera inspection is the best way to find the underlying cause." },
      { id: "drain-roots", q: "Can tree roots get into my drain lines?", a: "Yes — roots seek moisture and enter through joints and cracks, especially in older clay or cast-iron sewer lines. We can cut them out and camera the line to assess the damage." },
      { id: "drain-snake-vs-jet", q: "What's the difference between snaking and hydro-jetting?", a: "Snaking punches through a clog to restore flow. Hydro-jetting uses high-pressure water to scour the pipe walls clean, removing grease, sludge, and roots more thoroughly. Snaking is great for a quick clear; jetting is better for buildup and prevention." },
      { id: "drain-damage", q: "Will drain cleaning damage my pipes?", a: "Proper mechanical cleaning is safe for pipes in sound condition. If a line is already cracked or badly corroded, we'll usually spot it on camera and talk through the right fix rather than force it." },
      { id: "drain-outdoor", q: "Do you clear outdoor drains, yard drains, and downspout lines?", a: "Yes — we clear exterior area drains, yard drains, and buried downspout lines, and we can excavate and replace them if they're collapsed." },
    ],
  },
  {
    name: "Sewer Camera Inspection",
    faqs: [
      { id: "cam-what-is", q: "What is a sewer camera inspection?", a: "We feed a waterproof video camera through your drain or sewer line to see the inside of the pipe in real time — locating clogs, roots, cracks, bellies, and other defects." },
      { id: "cam-why", q: "Why would I need a camera inspection?", a: "To pinpoint the exact cause and location of a problem before spending money on a repair, or to confirm the condition of a line for peace of mind." },
      { id: "cam-prepurchase", q: "Should I get a camera inspection before buying a house?", a: "Yes — a pre-purchase sewer scope is one of the smartest inspections a buyer can get. Sewer line repairs are expensive, and they're usually not covered by a standard home inspection." },
      { id: "cam-where", q: "Can you tell me exactly where the problem is?", a: "In most cases, yes. We can locate the depth and position of the defect from above ground so any repair can be targeted instead of guessed at." },
      { id: "cam-video", q: "Do I get video or photos from the inspection?", a: "Yes — we can provide a recording and/or still images of the inspection on request." },
      { id: "cam-finds", q: "What problems do camera inspections commonly find?", a: "Root intrusion, grease and scale buildup, cracked or collapsed pipe, offset joints, bellies (low spots that hold water), and misconnections." },
      { id: "cam-cost", q: "How much does a camera inspection cost?", a: "All services are quoted on a per-case basis — call (240) 332-5647 or fill out a Quote Form, and the fee is often credited toward any resulting repair." },
      { id: "cam-blocked", q: "Can you inspect a line that's completely blocked?", a: "Usually we clear the blockage first, then run the camera so we can see the pipe condition clearly." },
    ],
  },
  {
    name: "Hydro-Jetting",
    faqs: [
      { id: "jet-what-is", q: "What is hydro-jetting?", a: "It's a cleaning method that uses a specialized nozzle and high-pressure water to blast the inside of the pipe clean — removing grease, sludge, scale, and small roots." },
      { id: "jet-vs-snake", q: "When is hydro-jetting the better choice over snaking?", a: "When there's heavy grease or sludge buildup, recurring clogs, or you want to fully clean the pipe rather than just punch a hole through the clog." },
      { id: "jet-old-pipes", q: "Is hydro-jetting safe for older pipes?", a: "For pipes in sound condition, yes. On older or fragile lines we usually camera first to confirm the pipe can handle it and adjust pressure accordingly." },
      { id: "jet-roots-grease", q: "Can hydro-jetting remove roots and grease?", a: "Yes — it's very effective on grease and can cut through smaller root intrusions. Heavy roots may still need mechanical cutting or excavation." },
      { id: "jet-how-often", q: "How often should I have a line hydro-jetted?", a: "It depends on use, but many commercial kitchens and grease-prone lines benefit from a scheduled cleaning (every 6–12 months). Most homes only need it as issues arise." },
      { id: "jet-cost", q: "How much does hydro-jetting cost?", a: "All services are quoted on a per-case basis — call (240) 332-5647 or fill out a Quote Form, and the fee is often credited toward any resulting repair." },
    ],
  },
  {
    name: "Basement Waterproofing",
    faqs: [
      { id: "wp-what-is", q: "What is basement waterproofing?", a: "It's a system of drainage and sealing solutions that keeps water from entering your basement or manages it before it causes damage." },
      { id: "wp-why-wet", q: "Why is my basement leaking or wet?", a: "Common causes are hydrostatic pressure in the soil, poor exterior grading, failed or missing drainage, foundation wall cracks, clogged footer drains, or a failed sump pump." },
      { id: "wp-methods", q: "What waterproofing methods do you offer?", a: "We offer interior and/or exterior solutions — interior drainage systems, sump pumps, exterior excavation and membrane sealing, crack repair, and grading/drainage improvements." },
      { id: "wp-french-drain", q: "What is an interior French drain (drain tile)?", a: "It's a perforated drainage channel installed around the interior perimeter of the basement floor that collects water and routes it to a sump pump, keeping the floor and walls dry." },
      { id: "wp-sump-pump", q: "What is a sump pump and do I need one?", a: "A sump pump sits in a pit at the low point of your basement and pumps collected water away from the house. If your basement takes on water or you have an interior drainage system, you need one." },
      { id: "wp-install-sump", q: "Do you install and replace sump pumps?", a: "Yes — we install and replace sump pumps, and we can add battery backup systems so the pump keeps working during power outages." },
      { id: "wp-cracks", q: "Can you fix foundation wall cracks that leak?", a: "Yes — we repair leaking cracks with the appropriate sealing method and address the drainage causing the pressure behind them." },
      { id: "wp-interior-vs-exterior", q: "Do I need interior waterproofing or exterior drainage — how do I know?", a: "It depends on where and how water is entering and on your grading and soil. We assess the source first, then recommend the approach that actually fixes the cause rather than just the symptom." },
      { id: "wp-warranty", q: "Does your waterproofing work come with a warranty?", a: "It depends — our waterproofing systems can come with a warranty. Call (240) 332-5647 to ask us for the specifics for your job." },
      { id: "wp-how-long", q: "How long does a waterproofing job take?", a: "Most interior systems take 1–3 days; exterior excavation projects can take longer depending on scope and access." },
      { id: "wp-mold", q: "Will waterproofing stop mold and musty smells?", a: "Controlling the water is the essential first step. Once the moisture source is managed, existing mold can be addressed and the musty smell typically clears." },
      { id: "wp-yard-water", q: "Can you help with standing water in the yard around the house?", a: "Yes — poor yard drainage often drives basement water. We can regrade, install exterior drainage, and bury downspout lines to move water away from the foundation." },
    ],
  },
  {
    name: "Mini Excavator & Excavation Work",
    faqs: [
      { id: "exc-what", q: "What excavation work do you do?", a: "Sewer and drain line dig-outs and replacements, drainage trenching (French drains, downspout lines), exterior waterproofing excavation, and related digging with our mini excavator." },
      { id: "exc-replace-sewer", q: "Can you dig up and replace a broken sewer line?", a: "Yes — when a line is collapsed or too damaged to clear, we excavate and replace the affected section (or the full run) and restore flow." },
      { id: "exc-open-trench", q: "What is an open-trench sewer repair?", a: "It's the traditional method of excavating down to the damaged pipe, replacing it, and backfilling. It's often the most direct fix when a line is collapsed or badly offset." },
      { id: "exc-trenching", q: "Do you do trenching for French drains and yard drainage?", a: "Yes — we trench and install drainage systems to move water away from your foundation and low spots in the yard." },
      { id: "exc-mini-fit", q: "Can a mini excavator fit in a tight backyard or through a gate?", a: "Yes — that's the advantage of a mini excavator. It fits through narrow gates and tight side yards where full-size equipment can't, with less disruption." },
      { id: "exc-lawn", q: "Will excavation tear up my lawn or driveway?", a: "Any dig disturbs the surface, but a mini excavator minimizes the footprint. We backfill and restore the area and can discuss final grading and finish restoration as part of the job." },
      { id: "exc-utilities", q: "Do you locate underground utilities before digging?", a: "Always. We arrange a Miss Utility (811) locate before any excavation so gas, water, electric, and other lines are marked. It's required by law in Maryland and it protects you and us." },
      { id: "exc-permit", q: "Do I need a permit for excavation or a sewer replacement?", a: "Many sewer and drainage jobs require a permit. We pull the required permits as part of the job and advise you on what's needed." },
      { id: "exc-exterior-wp", q: "Do you do exterior excavation for basement waterproofing?", a: "Yes — for exterior waterproofing we can excavate down to the foundation wall to seal it and address the footer drainage." },
      { id: "exc-window-well", q: "Can you install or dig out window wells and egress?", a: "Yes — we handle window well and related excavation. Ask us about your specific project." },
      { id: "exc-deep", q: "How deep can you dig?", a: "It depends on the machine and site conditions. Our mini excavator handles typical residential sewer and drainage depths; we'll confirm feasibility during the estimate." },
    ],
  },
  {
    name: "Pricing, Estimates & Warranties",
    faqs: [
      { id: "price-drain-cost", q: "How much does drain cleaning cost?", a: "All services are quoted on a per-case basis — call (240) 332-5647 or fill out a Quote Form, and the fee is often credited toward any resulting repair." },
      { id: "price-estimate-charge", q: "Do you charge for estimates?", a: "Estimates are free for most work. Diagnostic camera inspections may carry a fee that's credited toward the repair." },
      { id: "price-quotes-differ", q: "Why are quotes so different between companies?", a: "Scope and quality vary — some quotes only punch through a clog, while others include a camera inspection, full cleaning, or a real repair. We're specific about what's included so you can compare fairly." },
      { id: "price-financing", q: "Do you offer financing?", a: "Yes — we can offer third party financing on larger projects like waterproofing and excavation. Call (240) 332-5647 to ask us for details." },
      { id: "price-payment", q: "What payment methods do you accept?", a: "Cash, check, and major credit/debit cards." },
    ],
  },
  {
    name: "Scheduling & What to Expect",
    faqs: [
      { id: "sched-how-soon", q: "How soon can you come out?", a: "We often offer same-day or next-day service. Call (240) 332-5647 early for the best chance at a same-day slot." },
      { id: "sched-same-day", q: "Do you offer same-day service?", a: "Yes, when our schedule allows — especially for active backups." },
      { id: "sched-before", q: "What should I do before you arrive?", a: "Clear access to the affected drain, sink cabinet, or basement area if you can, and stop running water into a backed-up line. That's it — we'll handle the rest." },
      { id: "sched-be-home", q: "Do I need to be home during the service?", a: "We prefer someone 18+ be present to approve the work and provide access, but ask us — we can sometimes arrange alternatives." },
      { id: "sched-cant-clear", q: "What happens if you can't clear the clog?", a: "If a line can't be cleared mechanically, that usually means a deeper defect. We'll camera the line, show you what's going on, and lay out the repair options rather than keep charging to fight it." },
      { id: "sched-cleanup", q: "Do you clean up after the job?", a: "Yes — we clean the work area before we leave. For excavation, we backfill and restore the surface (per the agreed scope)." },
    ],
  },
  {
    name: "Prevention, Maintenance & DIY",
    faqs: [
      { id: "prev-prevent", q: "How can I prevent clogs?", a: "Keep grease, coffee grounds, and food scraps out of drains; use drain screens for hair; and never flush wipes or paper towels. Periodic cleaning of grease-prone lines also helps." },
      { id: "prev-never-put", q: "What should I never put down my drains or toilet?", a: "Grease and oil, coffee grounds, eggshells, fibrous foods, \"flushable\" wipes, paper towels, feminine products, and cat litter. When in doubt, throw it in the trash." },
      { id: "prev-wipes", q: "Are \"flushable\" wipes actually flushable?", a: "No — despite the label, they don't break down like toilet paper and are a leading cause of clogs and sewer backups." },
      { id: "prev-how-often", q: "How often should I have my drains cleaned?", a: "Most homes only need it when problems appear. Homes with big trees near the sewer line, older pipes, or a history of backups benefit from annual maintenance and periodic camera checks." },
      { id: "prev-warning-signs", q: "What are the warning signs of a sewer line problem?", a: "Multiple slow or gurgling drains, sewage odor, water backing up at the lowest fixture, unusually green patches or sinkholes in the yard, and frequent repeat clogs." },
      { id: "prev-enzyme", q: "Should I use enzyme or bacterial drain treatments?", a: "Enzyme-based maintenance products are gentler than harsh chemicals and can help keep a clean line clear, but they won't clear an existing clog — that needs mechanical cleaning." },
    ],
  },
];

// Flat lookup: id -> FAQ object (with its category name attached).
export const FAQ_BY_ID = FAQ_CATEGORIES.reduce((acc, cat) => {
  cat.faqs.forEach((f) => { acc[f.id] = { ...f, category: cat.name }; });
  return acc;
}, {});

// Helper: resolve an ordered list of FAQ ids to FAQ objects (skips unknown ids).
export const getFaqsByIds = (ids) => ids.map((id) => FAQ_BY_ID[id]).filter(Boolean);

// Home "quick answers" — a curated shortlist drawn from the bank above.
export const TOP_FAQS = getFaqsByIds([
  "gen-services",
  "gen-licensed-plumber",
  "gen-free-estimates",
  "gen-emergencies",
  "cam-prepurchase",
  "gen-areas",
]);

// Per-service FAQ sets — each service page pulls its most relevant questions
// from the central bank. Falls back to a general set for unmapped slugs.
const DEFAULT_SERVICE_FAQ_IDS = ["gen-services", "gen-licensed-insured", "gen-free-estimates", "sched-how-soon", "gen-emergencies"];

export const SERVICE_FAQ_MAP = {
  "drain-cleaning": ["drain-what-is", "drain-causes", "drain-small-vs-sewer", "drain-snake-vs-jet", "drain-keeps-clogging", "drain-chemical", "sched-how-soon", "price-drain-cost"],
  "sewer-camera-inspection": ["cam-what-is", "cam-why", "cam-prepurchase", "cam-where", "cam-finds", "cam-blocked", "cam-cost"],
  "hydro-jetting": ["jet-what-is", "jet-vs-snake", "jet-old-pipes", "jet-roots-grease", "jet-how-often", "jet-cost"],
  "pipe-descaling": ["jet-old-pipes", "drain-damage", "drain-roots", "jet-vs-snake", "cam-what-is", "price-estimate-charge"],
  "sewer-waterline-replacement": ["exc-replace-sewer", "exc-open-trench", "cam-where", "exc-utilities", "exc-permit", "price-quotes-differ", "price-financing"],
  "sump-pump-replacement": ["wp-sump-pump", "wp-install-sump", "wp-what-is", "sched-how-soon", "price-financing"],
  "basement-water-pumping": ["wp-why-wet", "wp-yard-water", "wp-sump-pump", "sched-how-soon", "gen-emergencies"],
  "basement-waterproofing": ["wp-what-is", "wp-why-wet", "wp-methods", "wp-french-drain", "wp-interior-vs-exterior", "wp-mold", "wp-warranty", "wp-how-long", "price-financing"],
  "underground-gutters": ["drain-outdoor", "wp-yard-water", "exc-trenching", "sched-how-soon"],
  "mini-excavator-service": ["exc-what", "exc-mini-fit", "exc-lawn", "exc-utilities", "exc-permit", "exc-deep", "exc-trenching"],
  "junk-trash-removal": DEFAULT_SERVICE_FAQ_IDS,
};

export const getServiceFaqs = (slug) => getFaqsByIds(SERVICE_FAQ_MAP[slug] || DEFAULT_SERVICE_FAQ_IDS);

export const SERVICE_OPTIONS = SERVICES.map((s) => s.name).concat(["Other / Not sure"]);

// =============================================================================
// PER-SERVICE CUSTOM CONTENT — unique, SEO-optimized copy for every service
// page. Keyed by service slug. Consumed by ServiceDetail.jsx and SeoManager.jsx.
//   metaTitle / metaDescription — per-page SEO tags
//   whatIs  — array of intro paragraphs ("What is X")
//   signs   — service-specific warning signs
//   why     — service-specific reasons to choose Drain Sights
// =============================================================================
export const SERVICE_CONTENT = {
  "drain-cleaning": {
    metaTitle: "Drain Cleaning in Baltimore, MD | Fast Clog Removal",
    metaDescription: "Slow or clogged drains cleared fast across the Baltimore & DC metros. Kitchen, bath, and main-line drain cleaning by a licensed contractor. Free estimates, 24/7 service.",
    whatIs: [
      "Drain cleaning is the mechanical clearing of a slow or clogged drain. Using a cable auger (a \"snake\") sized to the line, we break up or pull out the blockage — grease, hair, soap scum, food waste, or roots — and restore full flow. It's the safest, most reliable way to reopen a drain without the pipe damage that harsh chemical cleaners can cause.",
      "From a single slow bathroom sink to a backed-up main line serving the whole house, we clear it — and when it makes sense, we run a camera afterward to confirm the cause so the clog doesn't keep coming back.",
    ],
    signs: [
      "One or more drains emptying slowly",
      "Gurgling from sinks, tubs, or toilets",
      "Water backing up at the lowest fixture",
      "The same drain clogging over and over",
      "Sewage odor near a floor drain",
      "Multiple fixtures backing up at once",
    ],
    why: [
      "Mechanical clearing that's safe for sound pipes — no caustic chemicals",
      "Camera confirmation available to find the real cause",
      "Kitchen, bathroom, and main sewer lines all handled",
      "Upfront, per-job pricing before we start",
      "24/7 emergency response for active backups",
    ],
  },
  "sewer-camera-inspection": {
    metaTitle: "Sewer Camera Inspection & Line Locating | Baltimore, MD",
    metaDescription: "See inside your sewer line with a real-time camera inspection. We locate clogs, roots, cracks, and bellies — and pinpoint the exact spot. Pre-purchase sewer scopes available.",
    whatIs: [
      "A sewer camera inspection sends a waterproof video camera through your drain or sewer line so we can see the inside of the pipe in real time. Instead of guessing, we watch for clogs, tree roots, grease buildup, cracks, offset joints, and bellies (low spots that hold water) — and identify exactly what's wrong and where.",
      "We can locate the depth and position of a defect from above ground, so any repair is targeted rather than a blind dig. A pre-purchase sewer scope is one of the smartest inspections a home buyer can get, since sewer repairs are costly and aren't covered by a standard home inspection.",
    ],
    signs: [
      "Recurring clogs with no obvious cause",
      "Buying or selling a home (pre-purchase scope)",
      "Mature trees growing near the sewer line",
      "Older clay or cast-iron sewer piping",
      "Slow drains that return after previous repairs",
      "Soggy or sunken spots in the yard",
    ],
    why: [
      "Real-time video you can watch — recordings available on request",
      "Above-ground line locating for targeted repairs",
      "Honest diagnosis before you spend on a fix",
      "Trusted pre-purchase sewer scopes for buyers",
      "Inspection fee often credited toward the repair",
    ],
  },
  "hydro-jetting": {
    metaTitle: "Hydro-Jetting in Baltimore, MD | High-Pressure Drain Cleaning",
    metaDescription: "Hydro-jetting scours grease, sludge, scale, and roots from your pipes with high-pressure water — a deeper clean than snaking for recurring clogs. Free estimates, 24/7.",
    whatIs: [
      "Hydro-jetting cleans the inside of a pipe with a specialized nozzle and high-pressure water. Where a snake punches a hole through a clog, jetting scours the full pipe wall — flushing out years of grease, sludge, scale, and small root intrusions so the line drains like new.",
      "It's the better choice for heavy grease buildup, recurring clogs, and grease-prone commercial lines. On older or fragile pipes we camera the line first to confirm it can handle the pressure, then adjust accordingly.",
    ],
    signs: [
      "Clogs that keep returning after snaking",
      "Grease buildup in kitchen or restaurant lines",
      "Slow drainage across several fixtures",
      "Foul odors from built-up sludge",
      "Scale narrowing the inside of older pipes",
      "Minor root intrusion at pipe joints",
    ],
    why: [
      "Cleans the entire pipe wall, not just a hole through the clog",
      "Highly effective on grease, sludge, and scale",
      "Camera-first approach on older lines for safety",
      "Great for prevention on grease-prone lines",
      "Upfront pricing, with inspection often credited",
    ],
  },
  "pipe-descaling": {
    metaTitle: "Pipe Descaling in Baltimore, MD | Restore Cast-Iron Pipes",
    metaDescription: "Mechanical pipe descaling removes rust and scale from old cast-iron drain lines, restoring flow close to original diameter. Licensed contractor, free estimates.",
    whatIs: [
      "Pipe descaling mechanically removes the rust, scale, and hardened buildup that collects inside aging cast-iron drain lines. Over decades that buildup narrows the pipe, snags debris, and causes repeat clogs. Descaling restores the interior close to its original diameter and smoothness so waste flows freely again.",
      "We camera the line first to confirm the pipe is sound enough to descale, then clean it and re-inspect — often extending the life of old cast iron and delaying a costly replacement.",
    ],
    signs: [
      "Home still has original cast-iron drain piping",
      "Frequent clogs in the same older line",
      "Rough, rusty buildup visible on camera",
      "Slow drains throughout an older home",
      "Debris catching at the same spot repeatedly",
      "Weighing repiping and want options first",
    ],
    why: [
      "Restores old cast-iron lines toward full flow",
      "Camera inspection before and after the work",
      "Can delay or avoid a full pipe replacement",
      "Focused, specialist drain & sewer work",
      "Clear, upfront pricing per job",
    ],
  },
  "sewer-waterline-replacement": {
    metaTitle: "Sewer & Water Line Replacement | Baltimore, MD",
    metaDescription: "Cracked, collapsed, or root-damaged sewer and water lines replaced and restored. Camera-verified diagnosis, open-trench excavation, permits handled. Free estimates.",
    whatIs: [
      "When a sewer or water line is cracked, collapsed, offset, or root-damaged beyond cleaning, replacement is the permanent fix. We confirm the problem and its exact location with a camera inspection first, then excavate and replace the affected section — or the full run — and restore proper flow.",
      "As a licensed contractor we pull the required permits, arrange a Miss Utility (811) locate before digging, and backfill and restore the site when the work is done. If a job needs a licensed plumber for supply-side or fixture work, we'll tell you.",
    ],
    signs: [
      "Repeated main-line backups",
      "Sewage odor or backups at the lowest drain",
      "Sinkholes or soggy patches over the line",
      "Camera shows a collapsed or offset pipe",
      "Old clay or Orangeburg piping that's failing",
      "Water-line leaks or unexplained wet spots",
    ],
    why: [
      "Camera-verified diagnosis before any digging",
      "Permits and 811 utility locates handled for you",
      "Open-trench replacement done right and restored",
      "Full-run or spot replacement as needed",
      "Financing available on larger projects",
    ],
  },
  "sump-pump-replacement": {
    metaTitle: "Sump Pump Replacement in Baltimore, MD | Keep Basements Dry",
    metaDescription: "Worn or failed sump pump? We replace sump pumps and add battery backups so your basement stays dry through the next storm. Licensed contractor, free estimates.",
    whatIs: [
      "A sump pump sits in a pit at the low point of your basement and pumps collected groundwater away from the house. When it wears out, sticks, or can't keep up, the next heavy rain can mean a flooded basement. We replace failed and aging pumps with properly sized units and test the full system.",
      "We can also add a battery backup system so the pump keeps working during the power outages that often accompany big storms — exactly when you need it most.",
    ],
    signs: [
      "Pump runs constantly or won't shut off",
      "Pump stays silent during heavy rain",
      "Strange noises or visible rust on the unit",
      "Basement water despite a working pit",
      "Pump is more than 7–10 years old",
      "No battery backup during power outages",
    ],
    why: [
      "Correctly sized pump for your basement",
      "Battery backup options for storm outages",
      "Full system test after installation",
      "Paired with drainage or waterproofing when needed",
      "Upfront pricing, financing on larger jobs",
    ],
  },
  "basement-water-pumping": {
    metaTitle: "Basement Water Pumping in Baltimore, MD | Fast Cleanup",
    metaDescription: "Standing water in your basement after a backup or storm? We pump it out fast, then help find and fix the cause. 24/7 emergency response across the Baltimore & DC metros.",
    whatIs: [
      "When a backup or heavy storm leaves standing water in your basement, the first priority is getting it out quickly before it damages more of your home. We respond fast, pump out the water, and clear the area so drying can begin.",
      "Pumping is only step one. Once the water's gone, we help you find the source — a failed sump pump, clogged footer drain, sewer backup, or grading issue — and lay out the right fix so it doesn't happen again.",
    ],
    signs: [
      "Standing water on the basement floor",
      "A sump pump that failed during a storm",
      "Sewage backup into a basement drain",
      "Water intrusion after heavy rain",
      "A musty smell and lingering dampness",
      "Repeat flooding in the same spot",
    ],
    why: [
      "Fast 24/7 emergency response",
      "Water removed before more damage sets in",
      "Root-cause diagnosis after pumping",
      "A clear path to a permanent fix",
      "Local crews across both metros",
    ],
  },
  "basement-waterproofing": {
    metaTitle: "Basement Waterproofing in Baltimore, MD | Stop Leaks for Good",
    metaDescription: "Wet basement? We diagnose the source and fix it — interior French drains, sump pumps, crack repair, exterior sealing, and drainage. Warranties available. Free estimates.",
    whatIs: [
      "Basement waterproofing is a system of drainage and sealing solutions that either keeps water out of your basement or manages it before it can cause damage. The right approach depends on how and where water is getting in — hydrostatic pressure, poor grading, failed footer drains, foundation cracks, or a struggling sump pump.",
      "We assess the source first, then recommend interior solutions (perimeter French drains and sump systems), exterior solutions (excavation and membrane sealing), crack repair, and grading fixes — so we solve the cause, not just the symptom. Our waterproofing systems can come with a warranty.",
    ],
    signs: [
      "Water or seepage along the floor-and-wall joint",
      "Efflorescence (white residue) on the walls",
      "A musty smell or visible mold",
      "Foundation wall cracks that weep water",
      "Standing water after heavy rain",
      "Grading that slopes toward the house",
    ],
    why: [
      "Source-first diagnosis, not guesswork",
      "Interior and exterior solutions available",
      "Warranty options on waterproofing systems",
      "In-house excavation for exterior work",
      "Financing available on larger projects",
    ],
  },
  "underground-gutters": {
    metaTitle: "Underground Gutter & Downspout Drain Service | Baltimore, MD",
    metaDescription: "Clogged or broken buried downspout lines cleared, repaired, or replaced. We move roof water away from your foundation to help keep basements dry. Free estimates, 24/7.",
    whatIs: [
      "Underground gutters — the buried lines that carry water from your downspouts away from the house — are easy to forget until they clog or collapse. When they fail, roof water pools against the foundation and often ends up in the basement. We clear, repair, and replace these buried downspout and area-drain lines.",
      "If a line is crushed or collapsed, we can excavate and replace it, and re-route the discharge so water exits well away from your foundation and the low spots in your yard.",
    ],
    signs: [
      "Water pooling around the foundation",
      "Downspouts backing up in heavy rain",
      "Gurgling or overflow at the downspout base",
      "A wet basement after storms",
      "Eroded or sunken soil along a buried line",
      "Ice or backups at the drain outlet",
    ],
    why: [
      "Clears and replaces buried downspout lines",
      "Keeps roof water away from the foundation",
      "Excavation and re-routing when lines collapse",
      "Pairs naturally with basement waterproofing",
      "Upfront, per-job pricing",
    ],
  },
  "mini-excavator-service": {
    metaTitle: "Mini Excavator & Bobcat Service in Baltimore, MD | Sewer Digs",
    metaDescription: "Compact excavation for sewer line dig-outs, drainage trenching, French drains, and exterior waterproofing. Fits tight yards and gates. 811 locates and permits handled.",
    whatIs: [
      "Our mini excavator and compact Bobcat handle the digging behind sewer, drainage, and waterproofing work — sewer and drain line dig-outs and replacements, French-drain and downspout trenching, and exterior foundation excavation. Compact equipment fits through narrow gates and tight side yards where full-size machines can't go, with far less disruption to your property.",
      "Before any dig we arrange a Miss Utility (811) locate so gas, water, and electric lines are marked, and we pull the permits the job requires. When the work is done, we backfill and restore the surface per the agreed scope.",
    ],
    signs: [
      "A collapsed sewer line that needs excavation",
      "Trenching required for a French drain",
      "Exterior foundation waterproofing is needed",
      "Buried downspout lines must be replaced",
      "A tight-access or gated backyard",
      "Drainage regrading around the home",
    ],
    why: [
      "Fits tight gates and narrow side yards",
      "811 utility locates arranged before digging",
      "Permits pulled and the site restored",
      "Minimal footprint and thorough cleanup",
      "One crew for the dig and the drainage work",
    ],
  },
  "junk-trash-removal": {
    metaTitle: "Junk & Trash Removal in Baltimore, MD | Haul-Away Service",
    metaDescription: "Haul-away for construction debris, old fixtures, and basement cleanout junk across the Baltimore & DC metros. Fast, tidy removal alongside our drain & sewer work.",
    whatIs: [
      "Alongside our drain, sewer, and excavation work, we haul away the debris that projects leave behind — old fixtures, broken pipe and concrete, excavated spoil, and basement-cleanout junk. It's a convenient add-on so you're not left with a pile after the real work is done.",
      "We can also handle standalone cleanouts. Tell us what needs to go and we'll give you a clear, upfront price for the haul-away.",
    ],
    signs: [
      "Debris left after a repair or excavation",
      "Old fixtures or appliances to remove",
      "A basement or garage cleanout",
      "Broken concrete, pipe, or spoil piles",
      "Post-project site cleanup needed",
      "Bulk items too big for regular trash",
    ],
    why: [
      "Convenient add-on to drain & sewer jobs",
      "Standalone cleanouts welcome",
      "Clear, upfront haul-away pricing",
      "A tidy site when we leave",
      "Local crews across both metros",
    ],
  },
};

export const getServiceContent = (slug) => SERVICE_CONTENT[slug] || null;

// =============================================================================
// PER-AREA CUSTOM CONTENT — unique local copy for every service-area page.
// Keyed by area slug. Consumed by ServiceAreaDetail.jsx and SeoManager.jsx.
//   metaTitle / metaDescription — per-page SEO tags
//   intro       — array of local intro paragraphs
//   localIssues — common drain & sewer issues seen in that community
// =============================================================================
export const AREA_CONTENT = {
  "baltimore-md": {
    metaTitle: "Drain Cleaning & Sewer Service in Baltimore, MD | Drain Sights",
    metaDescription: "Drain cleaning, sewer camera inspection, hydro-jetting and sewer line work for Baltimore homes. Rowhome-savvy crews for old clay & cast-iron lines. 24/7, free estimates.",
    intro: [
      "Baltimore is our north-side home base, and its housing stock keeps us busy. Block after block of classic rowhomes and pre-war houses means a lot of original clay and cast-iron sewer laterals — pipe that's prone to scale, cracks, and root intrusion after decades in the ground. We know how these older lines behave and how to clear and inspect them without making things worse.",
      "From Federal Hill and Canton to the neighborhoods ringing the city, we handle everything from a slow kitchen sink to a collapsed main line. We answer 24/7, give you a clear price up front, and camera the line whenever it helps pin down a recurring problem.",
    ],
    localIssues: [
      "Root intrusion in old clay sewer laterals",
      "Scale buildup inside aging cast-iron drains",
      "Shared party-wall rowhome line complications",
      "Recurring main-line backups in older blocks",
      "Grease clogs in kitchen lines",
    ],
  },
  "dundalk-md": {
    metaTitle: "Drain & Sewer Service in Dundalk, MD | Drain Sights",
    metaDescription: "Local drain cleaning, camera inspection, hydro-jetting and sewer repair for Dundalk homes. Mid-century pipe experts near the water. 24/7 emergency service, free estimates.",
    intro: [
      "Dundalk's tidy mid-century neighborhoods went up largely in the postwar decades, which means many homes are now working with sewer and drain lines that are 60–70 years old. Cast-iron stacks and clay laterals from that era commonly develop scale, bellies, and root intrusion — the usual suspects behind the slow drains and repeat backups we see here.",
      "Sitting close to the Patapsco, parts of Dundalk also deal with higher water tables and wet basements. We clear drains, camera lines, and tackle the sump pump and drainage work that keeps lower levels dry.",
    ],
    localIssues: [
      "Aging postwar cast-iron and clay lines",
      "Root intrusion from mature neighborhood trees",
      "Wet basements in low-lying, waterside areas",
      "Sump pump failures during heavy storms",
      "Recurring clogs from pipe bellies",
    ],
  },
  "glen-burnie-md": {
    metaTitle: "Drain & Sewer Service in Glen Burnie, MD | Drain Sights",
    metaDescription: "Drain cleaning, sewer camera inspection and line repair for Glen Burnie, MD. Anne Arundel crews for mid-century and newer homes. 24/7, upfront pricing, free estimates.",
    intro: [
      "Glen Burnie's mix of mid-century subdivisions and newer construction means we see the full range of drain and sewer setups across Anne Arundel County — from older clay laterals to modern PVC. That variety is exactly why we camera a line before making assumptions: it tells us what we're actually dealing with.",
      "Whether it's a stubborn kitchen clog, a main line backing up, or a sump pump that quit right before a storm, we show up 24/7 and quote the job clearly before we start.",
    ],
    localIssues: [
      "Clay sewer laterals in older subdivisions",
      "Root intrusion at pipe joints",
      "Grease and food buildup in kitchen lines",
      "Sump pump and basement water issues",
      "Main-line clogs from long-term buildup",
    ],
  },
  "catonsville-md": {
    metaTitle: "Drain & Sewer Service in Catonsville, MD | Drain Sights",
    metaDescription: "Drain cleaning, hydro-jetting and sewer camera inspection for Catonsville, MD. Specialists in older-home cast-iron & clay lines under mature trees. 24/7, free estimates.",
    intro: [
      "Catonsville is an established Baltimore County community with a lot of character — and a lot of mature trees and older homes. Those big, beautiful trees are also the number-one enemy of old clay sewer laterals: roots find their way into joints and cracks, then grow until the line backs up. Camera inspection and root cutting are everyday work for us here.",
      "We handle the aging cast-iron drains common in older Catonsville houses too, from descaling and hydro-jetting to full line replacement when a pipe is past saving.",
    ],
    localIssues: [
      "Tree-root intrusion in clay sewer lines",
      "Scale buildup in old cast-iron drains",
      "Recurring backups under mature-tree lots",
      "Slow drains throughout older homes",
      "Collapsed or offset aging laterals",
    ],
  },
  "pasadena-md": {
    metaTitle: "Drain & Sewer Service in Pasadena, MD | Drain Sights",
    metaDescription: "Drain cleaning, sewer camera inspection and drainage work for Pasadena, MD. Waterfront-peninsula crews for high-water-table homes. 24/7 emergency service, free estimates.",
    intro: [
      "Pasadena sits on the peninsula between the Magothy and Patapsco rivers, so water is never far away — and neither are high water tables and damp basements. Along with standard drain and sewer work, we do a lot of sump pump, drainage, and basement water work for homes close to the shoreline here.",
      "For the area's older properties, we camera lines to check for roots and pipe damage, and we jet grease-prone lines to keep them flowing. Whatever the issue, you'll get a clear price before we start.",
    ],
    localIssues: [
      "High water tables and wet basements",
      "Sump pump strain during heavy rain",
      "Root intrusion in older sewer laterals",
      "Yard drainage near waterfront lots",
      "Grease and buildup clogs in kitchen lines",
    ],
  },
  "arbutus-md": {
    metaTitle: "Drain & Sewer Service in Arbutus, MD | Drain Sights",
    metaDescription: "Fast drain cleaning, camera inspection and sewer repair for Arbutus, MD. Local Baltimore County crews for older-home clay & cast-iron lines. 24/7, free estimates.",
    intro: [
      "Arbutus is a close-knit Baltimore County community near Catonsville and UMBC, with a housing stock that leans older. That means plenty of clay sewer laterals and cast-iron drains that have been in the ground long enough to scale up, crack, or catch roots — the usual causes of the slow drains and repeat backups we clear here.",
      "Because we're right next door, response is quick. We answer 24/7, camera lines to find the real cause, and give you an upfront price before any work begins.",
    ],
    localIssues: [
      "Aging clay and cast-iron sewer lines",
      "Root intrusion from established trees",
      "Recurring clogs in older drains",
      "Grease buildup in kitchen lines",
      "Main-line backups needing camera diagnosis",
    ],
  },
  "washington-dc": {
    metaTitle: "Drain Cleaning & Sewer Service in Washington, DC | Drain Sights",
    metaDescription: "Drain cleaning, sewer camera inspection and hydro-jetting for Washington, DC rowhouses. Experts in very old clay/terra-cotta lines and root intrusion. 24/7, free estimates.",
    intro: [
      "Washington, DC has some of the oldest housing we service — historic rowhouses and townhomes sitting on clay and terra-cotta sewer lines that can be a century old. Age plus dense, mature street trees is a recipe for root intrusion, cracked pipe, and repeat main-line backups, so camera inspection and hydro-jetting are core to the work we do in the District.",
      "Tight rowhouse lots and shared alleys don't slow us down — our compact equipment and drain-and-sewer focus are built for exactly these conditions. You'll get a clear diagnosis and an upfront price.",
    ],
    localIssues: [
      "Century-old clay and terra-cotta sewers",
      "Heavy root intrusion from street trees",
      "Recurring backups in historic rowhouses",
      "Grease buildup in shared or older lines",
      "Cracked or offset pipe in aged laterals",
    ],
  },
  "clinton-md": {
    metaTitle: "Drain & Sewer Service in Clinton, MD | Drain Sights",
    metaDescription: "Drain cleaning, sewer camera inspection and line repair for Clinton, MD. Prince George's County crews for suburban homes. 24/7 emergency service, upfront pricing.",
    intro: [
      "Clinton is a suburban Prince George's County community built out largely through the mid-to-late 20th century. Homes from that era are now hitting the age where original sewer laterals start to fail — root intrusion, bellies, and cracked clay are common — and where cast-iron drains benefit from descaling or jetting.",
      "We're based just up the road in Temple Hills, so Clinton is core territory for us. We answer 24/7, camera the line to find the real problem, and quote the job clearly before we begin.",
    ],
    localIssues: [
      "Aging suburban sewer laterals",
      "Root intrusion at clay pipe joints",
      "Cast-iron scale and slow drains",
      "Main-line backups from buildup",
      "Sump pump and basement water issues",
    ],
  },
  "suitland-md": {
    metaTitle: "Drain & Sewer Service in Suitland, MD | Drain Sights",
    metaDescription: "Local drain cleaning, hydro-jetting and sewer camera inspection for Suitland, MD. Prince George's County crews for mid-century homes. 24/7, free estimates.",
    intro: [
      "Suitland's mid-century Prince George's County neighborhoods are served by sewer and drain lines that have been working for decades. That age brings the familiar issues — root intrusion in clay laterals, scale inside cast-iron drains, and buildup that leads to repeat clogs — all of which we clear, camera, and, when needed, replace.",
      "We're minutes away in Temple Hills, so help arrives fast. Expect a straight answer on what's wrong and a clear price before any work starts.",
    ],
    localIssues: [
      "Root intrusion in older clay laterals",
      "Scale buildup in cast-iron drains",
      "Recurring clogs from pipe buildup",
      "Grease clogs in kitchen lines",
      "Main-line backups needing a camera",
    ],
  },
  "fort-washington-md": {
    metaTitle: "Drain & Sewer Service in Fort Washington, MD | Drain Sights",
    metaDescription: "Drain cleaning, sewer camera inspection and drainage work for Fort Washington, MD. Crews for larger lots and riverside high-water-table homes. 24/7, free estimates.",
    intro: [
      "Fort Washington sits along the Potomac in Prince George's County, with larger lots, rolling terrain, and homes that range from mid-century to newer builds. Proximity to the river means higher water tables in spots, so alongside drain and sewer work we do plenty of sump pump, drainage, and basement water projects here.",
      "Longer runs from house to street are common on these bigger lots, which makes camera inspection and line locating especially valuable — we pinpoint the problem so any repair is targeted, not a guess.",
    ],
    localIssues: [
      "High water tables near the river",
      "Long sewer runs on larger lots",
      "Sump pump and basement water issues",
      "Root intrusion in older laterals",
      "Yard drainage and grading problems",
    ],
  },
  "camp-springs-md": {
    metaTitle: "Drain & Sewer Service in Camp Springs, MD | Drain Sights",
    metaDescription: "Drain cleaning, camera inspection and sewer repair for Camp Springs, MD. Prince George's County crews near Joint Base Andrews. 24/7 emergency service, free estimates.",
    intro: [
      "Camp Springs is an established Prince George's County community near Joint Base Andrews, with a housing stock that's largely mid-century. Sewer laterals and cast-iron drains from that period are now prone to scale, root intrusion, and the slow drains that come with decades of buildup — bread-and-butter work for our crews.",
      "Based nearby in Temple Hills, we respond fast, camera lines to confirm the cause, and give you an upfront price. Available 24/7 for backups and active leaks.",
    ],
    localIssues: [
      "Aging mid-century sewer laterals",
      "Root intrusion in clay pipe",
      "Cast-iron scale and slow drains",
      "Recurring main-line backups",
      "Grease buildup in kitchen lines",
    ],
  },
  "oxon-hill-md": {
    metaTitle: "Drain & Sewer Service in Oxon Hill, MD | Drain Sights",
    metaDescription: "Fast drain cleaning, hydro-jetting and sewer camera inspection for Oxon Hill, MD. Prince George's County crews near National Harbor. 24/7, upfront pricing, free estimates.",
    intro: [
      "Oxon Hill runs along the Potomac in Prince George's County near National Harbor, mixing older established neighborhoods with newer development. That range means anything from aging clay laterals to modern PVC, so we camera lines to see exactly what a home has before recommending a fix.",
      "Lower-lying areas near the river can bring wet basements and drainage headaches, so we pair our drain and sewer work with sump pump and waterproofing service. We're close by in Temple Hills and answer 24/7.",
    ],
    localIssues: [
      "Wet basements in low-lying areas",
      "Root intrusion in older clay laterals",
      "Sump pump strain during storms",
      "Grease and buildup clogs",
      "Main-line backups needing camera diagnosis",
    ],
  },
};

export const getAreaContent = (slug) => AREA_CONTENT[slug] || null;
